import { LanguageOption, SupportedLanguage, useTranslation } from '@/src/i18n';
import { NotificationType } from '@/src/domain/models/Notification';
import { LEGAL_URLS } from '@/src/core/config/legalUrls';
import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { ActivityIndicator, Linking, Modal, Platform, ScrollView, Switch, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './SettingsScreen.styles';

export interface SettingsScreenProps {
  isDark: boolean;
  toggleTheme: () => void;
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  supportedLanguages: LanguageOption[];
  colors: {
    background: string;
    surface: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    divider: string;
    primary: string;
    overlay: string;
    error: string;
  };
  notificationPreferences?: Record<NotificationType, boolean>;
  onToggleNotificationPreference?: (type: NotificationType, enabled: boolean) => void;
  pushPermissionStatus?: 'granted' | 'denied' | 'undetermined';
  onEnablePushNotifications?: () => void;
  onOpenSystemSettings?: () => void;
  onDeleteAccount?: () => void;
  deletingAccount?: boolean;
  appVersion: string;
}

const NOTIFICATION_TYPE_LABELS: { type: NotificationType; icon: string }[] = [
  { type: 'tournament_started', icon: 'play-circle' },
  { type: 'tournament_invitation', icon: 'person-add' },
  { type: 'friend_request', icon: 'people' },
  { type: 'challenge_received', icon: 'flash' },
  { type: 'match_result', icon: 'trophy' },
  { type: 'tournament_completed', icon: 'checkmark-circle' },
  { type: 'league_invitation', icon: 'ribbon' },
];

export const SettingsScreen = ({
  isDark,
  toggleTheme,
  language,
  setLanguage,
  supportedLanguages,
  colors,
  notificationPreferences,
  onToggleNotificationPreference,
  pushPermissionStatus,
  onEnablePushNotifications,
  onOpenSystemSettings,
  onDeleteAccount,
  deletingAccount,
  appVersion,
}: SettingsScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const [languageModalVisible, setLanguageModalVisible] = useState(false);

  const currentLanguage = supportedLanguages.find((l) => l.code === language);

  const handleLanguageSelect = (langCode: SupportedLanguage) => {
    setLanguage(langCode);
    setLanguageModalVisible(false);
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.section, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.sectionTitle, { color: colors.textTertiary }]}>{t('settings.appearance').toUpperCase()}</Text>

        <View style={[styles.settingItem, { borderBottomColor: colors.divider }]}>
          <View style={styles.settingLeft}>
            <Ionicons name={isDark ? 'moon' : 'sunny'} size={24} color={colors.text} style={styles.settingIcon} />
            <Text style={[styles.settingTitle, { color: colors.text }]}>{isDark ? t('settings.darkMode') : t('settings.lightMode')}</Text>
          </View>
          <Switch
            value={isDark}
            onValueChange={toggleTheme}
            trackColor={{ false: colors.divider, true: colors.primary + '80' }}
            thumbColor={isDark ? colors.primary : colors.textTertiary}
          />
        </View>

        <TouchableOpacity
          style={[styles.settingItem, { borderBottomWidth: 0 }]}
          onPress={() => setLanguageModalVisible(true)}
        >
          <View style={styles.settingLeft}>
            <Ionicons name="language" size={24} color={colors.text} style={styles.settingIcon} />
            <View>
              <Text style={[styles.settingTitle, { color: colors.text }]}>{t('settings.language')}</Text>
              <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>
                {currentLanguage?.nativeName || 'English'}
              </Text>
            </View>
          </View>
          <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
        </TouchableOpacity>
      </View>

      {notificationPreferences && onToggleNotificationPreference && (
        <View style={[styles.section, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Text style={[styles.sectionTitle, { color: colors.textTertiary }]}>{t('notifications.preferences').toUpperCase()}</Text>

          {Platform.OS !== 'web' && pushPermissionStatus !== undefined && (
            <View style={[styles.settingItem, { borderBottomColor: colors.divider }]}>
              <View style={styles.settingLeft}>
                <Ionicons name="notifications" size={24} color={colors.text} style={styles.settingIcon} />
                <Text style={[styles.settingTitle, { color: colors.text }]}>
                  {t('settings.pushNotifications')}
                </Text>
              </View>
              {pushPermissionStatus === 'granted' ? (
                <Text style={{ color: '#10B981', fontSize: 14, fontWeight: '600' }}>
                  {t('settings.pushNotificationsEnabled')}
                </Text>
              ) : pushPermissionStatus === 'denied' ? (
                <TouchableOpacity onPress={onOpenSystemSettings}>
                  <Text style={{ color: colors.primary, fontSize: 14, fontWeight: '600' }}>
                    {t('settings.pushNotificationsOpenSettings')}
                  </Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity onPress={onEnablePushNotifications}>
                  <Text style={{ color: colors.primary, fontSize: 14, fontWeight: '600' }}>
                    {t('settings.pushNotificationsEnable')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {NOTIFICATION_TYPE_LABELS.map((item, index) => (
            <View
              key={item.type}
              style={[
                styles.settingItem,
                { borderBottomColor: colors.divider },
                index === NOTIFICATION_TYPE_LABELS.length - 1 && { borderBottomWidth: 0 },
              ]}
            >
              <View style={styles.settingLeft}>
                <Ionicons name={item.icon as any} size={24} color={colors.text} style={styles.settingIcon} />
                <Text style={[styles.settingTitle, { color: colors.text }]}>
                  {t(`notifications.type_${item.type}`)}
                </Text>
              </View>
              <Switch
                value={notificationPreferences[item.type] ?? true}
                onValueChange={(value) => onToggleNotificationPreference(item.type, value)}
                trackColor={{ false: colors.divider, true: colors.primary + '80' }}
                thumbColor={notificationPreferences[item.type] ? colors.primary : colors.textTertiary}
              />
            </View>
          ))}
        </View>
      )}

      <View style={[styles.section, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.sectionTitle, { color: colors.textTertiary }]}>{t('settings.about').toUpperCase()}</Text>

        <View style={[styles.settingItem, { borderBottomColor: colors.divider }]}>
          <View style={styles.settingLeft}>
            <Ionicons name="information-circle" size={24} color={colors.text} style={styles.settingIcon} />
            <View>
              <Text style={[styles.settingTitle, { color: colors.text }]}>{t('settings.version')}</Text>
              <Text style={[styles.settingDescription, { color: colors.textSecondary }]}>{appVersion}</Text>
            </View>
          </View>
        </View>

        <TouchableOpacity
          style={[styles.settingItem, { borderBottomColor: colors.divider }]}
          onPress={() => Linking.openURL(LEGAL_URLS.termsOfService)}
        >
          <View style={styles.settingLeft}>
            <Ionicons name="document-text-outline" size={24} color={colors.text} style={styles.settingIcon} />
            <Text style={[styles.settingTitle, { color: colors.text }]}>{t('settings.termsOfService')}</Text>
          </View>
          <Ionicons name="open-outline" size={18} color={colors.textTertiary} />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.settingItem, { borderBottomWidth: 0 }]}
          onPress={() => Linking.openURL(LEGAL_URLS.privacyPolicy)}
        >
          <View style={styles.settingLeft}>
            <Ionicons name="shield-checkmark-outline" size={24} color={colors.text} style={styles.settingIcon} />
            <Text style={[styles.settingTitle, { color: colors.text }]}>{t('settings.privacyPolicy')}</Text>
          </View>
          <Ionicons name="open-outline" size={18} color={colors.textTertiary} />
        </TouchableOpacity>
      </View>

      {onDeleteAccount && (
        <View style={[styles.section, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <Text style={[styles.sectionTitle, { color: colors.textTertiary }]}>{t('settings.account').toUpperCase()}</Text>
          <TouchableOpacity
            style={[styles.settingItem, { borderBottomWidth: 0 }]}
            onPress={onDeleteAccount}
            disabled={deletingAccount}
          >
            <View style={styles.settingLeft}>
              <Ionicons name="trash-outline" size={24} color={colors.error} style={styles.settingIcon} />
              <Text style={[styles.settingTitle, { color: colors.error }]}>{t('settings.deleteAccount')}</Text>
            </View>
            {deletingAccount
              ? <ActivityIndicator size="small" color={colors.error} />
              : <Ionicons name="chevron-forward" size={20} color={colors.error} />
            }
          </TouchableOpacity>
        </View>
      )}

      {/* Language Selection Modal */}
      <Modal visible={languageModalVisible} transparent animationType="fade" onRequestClose={() => setLanguageModalVisible(false)}>
        <TouchableOpacity
          style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}
          activeOpacity={1}
          onPress={() => setLanguageModalVisible(false)}
        >
          <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]}>{t('settings.selectLanguage')}</Text>

            {supportedLanguages.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                style={[styles.languageOption, { borderBottomColor: colors.divider }]}
                onPress={() => handleLanguageSelect(lang.code)}
              >
                <View>
                  <Text style={[styles.languageName, { color: colors.text }]}>{lang.nativeName}</Text>
                  <Text style={[styles.languageNameSecondary, { color: colors.textSecondary }]}>{lang.name}</Text>
                </View>
                {language === lang.code && <Ionicons name="checkmark-circle" size={24} color={colors.primary} />}
              </TouchableOpacity>
            ))}

            <TouchableOpacity
              style={[styles.modalCancel, { borderTopColor: colors.divider }]}
              onPress={() => setLanguageModalVisible(false)}
            >
              <Text style={[styles.modalCancelText, { color: colors.primary }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </ScrollView>
  );
};
